import React, { useContext, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import _ from 'lodash';
import {
  EligibleLALayouts,
  HorizontalLaPositions,
  PlacementLossAversionPosition,
} from '@bbkAdminRedux/rtkq/placements.utils';
import {
  isLossAversionCard,
  type RestMultiRowSurveyCard,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import { type RestCard } from '@bbkAdminUtils/api-client/rest-experiences-api';
import { type RestLossAversionWithOffer } from '@bbkAdminUtils/api-client/rest-la-cards-types';
import {
  RestModalAction,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import {
  type EventObject,
  type EventSyncObject,
  type ExecuteEventsHandler,
  type NextStepHandler,
} from '@bbkFunnel/events';
import icons from '@bbkFunnel/utils/icons';
import { hasSameElements } from '@bbkFunnel/utils/shared';
import CardFactory from '@bbkFunnelComponents/card-factory';
import DesignElement from '@bbkFunnelComponents/design-element/design-element';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { EmptyLACard } from '@bbkFunnelComponents/loss/empty-loss-aversion-card';
import { useMediaQueries } from '@bbkFunnelComponents/modals/sections';
import { isCheckoutAction } from '@bbkFunnelUtils/cancel-checkout';
import { getModalCtaIdMap } from '@bbkFunnelUtils/cards-utils';
import {
  REFS_CONTEXT_FUNNEL_PREFIX,
  RefsContext,
} from '@bbkFunnelUtils/refs.context';

import './loss.scss';

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
  width: 100%;
`;

const LossComponentsGrid = styled.div<{ isMobile: boolean }>`
  position: relative;
  margin: 0 -10px 0 -10px;
  display: grid;
  ${({ isMobile }) =>
    isMobile
      ? 'grid-template-columns: repeat(1, 1fr);'
      : 'grid-template-columns: repeat(2, 1fr);'}
  grid-template-rows: repeat(2, auto);
  gap: 30px;
`;

type LACardContainerProps = {
  laCardDims: number[];
  needsGridRow: boolean;
};

const LossAversionCardContainer = styled.span<LACardContainerProps>`
  ${({ laCardDims }) => `grid-column: ${laCardDims[0]} / ${laCardDims[1]};`}
  ${({ laCardDims, needsGridRow }) =>
    needsGridRow ? `grid-row: ${laCardDims[2]} / ${laCardDims[3]};` : ''}
`;

const EmptyLACardLive = styled.div<{ isHorizontal: boolean }>`
  width: ${({ isHorizontal }) => (isHorizontal ? '100%' : '50%')};
`;

const getLaCardDims = (
  position: PlacementLossAversionPosition,
  isLong: boolean,
  isMobile: boolean
) => {
  if (isMobile) return [1, 2, 1, 2];
  switch (position) {
    case PlacementLossAversionPosition.loss_aversion_left:
      return [1, 2, 1, isLong ? 3 : 2];
    case PlacementLossAversionPosition.loss_aversion_right:
      return [2, 3, 1, isLong ? 3 : 2];
    case PlacementLossAversionPosition.loss_aversion_bottom_left:
      return [1, 2, 2, 3];
    case PlacementLossAversionPosition.loss_aversion_bottom_right:
      return [2, 3, 2, 3];
    case PlacementLossAversionPosition.loss_aversion_top_row:
      return [1, 3, 1, 2];
    case PlacementLossAversionPosition.loss_aversion_bottom_row:
      return [1, 3, 2, 3];
  }
};

type Props = {
  cards: RestCard[];
  syncData: {
    session: string;
    version: string;
    app_id: string | null;
    survey_state: RestMultiRowSurveyCard['provides'];
  };
  executeEvents: ExecuteEventsHandler;
  nextStep: NextStepHandler;
  continueAction: () => void;
  lossAversionLayout?: (typeof EligibleLALayouts)[number]['label'];
  isLossAversionLoading?: boolean;
  isLifecycle?: boolean;
  lossAversionErrors?: PlacementLossAversionPosition[];
};

export const Loss: React.FC<Props> = (props) => {
  const {
    cards: cardsP,
    lossAversionLayout,
    isLossAversionLoading,
    lossAversionErrors,
  } = props;
  const { mobileView } = useMediaQueries();
  const { isDesigner } = useContext(FunnelContext);
  const cards = cardsP.filter(isLossAversionCard).filter((cd) => {
    if (mobileView) {
      if (_.has(cd, 'requires.disableOnMobile')) {
        return !cd.requires.disableOnMobile;
      }
    }
    return true;
  });

  // Gives the option to launch a modal
  // or continue with an action directly
  const handleLossCardAction = async ({
    card,
    action,
  }: {
    card: RestLossAversionWithOffer;
    action: RestModalForwards;
  }): Promise<void> => {
    const { syncData, nextStep, continueAction, executeEvents } = props;
    const forwardsAction = action;
    const modalCtaIdMap = getModalCtaIdMap(cards);
    if (forwardsAction.cta_details_list?.length) {
      if (!forwardsAction.cta_details_list[0]?.cta_id) {
        GlobalLogger.error(
          `LA action (Navigation) exists with a non-zero cta_detail_list, but the ctaID of the first item is either blank or invalid. ${syncData.session}`
        );
      } else if (!forwardsAction.cta_details_list?.[0]?.cta_id) {
        GlobalLogger.error(
          `LA action (Navigation) exists with a non-zero cta_detail_list and ctaID but the ?.[].? operator is not working for some browsers. ${syncData.session}`
        );
      }
    } else {
      GlobalLogger.error(
        `LA action (Navigation) is null or empty. ${syncData.session}`
      );
      console.log(forwardsAction);
    }
    const formData = {
      sync: forwardsAction.sync,
      message: _.get(forwardsAction, 'sync.message'),
      survey_state: syncData.survey_state,
    };

    const commonKeys = {
      ..._.omit(syncData, 'survey_state'),
      card_version: card.version,
      direction: 'forwards' as 'forwards',
      id: card.id || null,
      name: card.name || card.id || 'loss_aversion_card',
      cta_ids: forwardsAction.cta_details_list?.map((cta) => cta.cta_id) ?? [
        modalCtaIdMap[card.name],
      ],
      cta_id:
        forwardsAction.cta_details_list?.[0]?.cta_id ??
        modalCtaIdMap[card.name],
    };

    const commonEvent: EventObject = {
      ...commonKeys,
      id: card.id || null,
      name: card.name || card.id || 'loss_aversion_card',
      event: RestModalAction[forwardsAction.action],
      url: forwardsAction.url,
      properties: {
        survey_state: syncData.survey_state,
      },
    };

    const syncObj: EventSyncObject = {
      ...commonKeys,
      action: forwardsAction.action,
      type: forwardsAction.type,
      modal: forwardsAction.modal,
      validation: forwardsAction.validation,
      url: forwardsAction.url,
      form: formData,
      survey_state: syncData.survey_state,
    };

    if (!props.isLifecycle && isCheckoutAction(forwardsAction)) {
      await executeEvents({
        syncs: [{ ...syncObj, isCheckoutProcessing: true }],
      });
      return;
    }

    const laAcceptedEventJSON = {
      ...commonEvent,
      event: RestModalAction.accepted_la_offer,
    };

    if (_.get(forwardsAction, 'action') === 'send_email') {
      // Allow us to track the email message as well
      await executeEvents({
        synchronous: true,
        events: [laAcceptedEventJSON, commonEvent],
        syncs: [
          {
            ...syncObj,
            action: RestModalAction.accept_offer,
          },
          syncObj,
        ],
      });
      continueAction();
      nextStep(_.get(forwardsAction, 'modal'));
      return;
    } else if (
      _.get(forwardsAction, 'action') === 'accept_offer_with_confirmation'
    ) {
      executeEvents({
        synchronous: true,
        events: [laAcceptedEventJSON],
        syncs: [
          {
            ...syncObj,
            action: RestModalAction.accept_offer,
          },
        ],
      });
      nextStep(
        forwardsAction.modal,
        _.get(forwardsAction, 'initial_message', {})
      );
      continueAction();
    } else if (_.has(forwardsAction, 'modal')) {
      nextStep(
        forwardsAction.modal,
        _.get(forwardsAction, 'initial_message', {})
      );
      continueAction();
    } else if (
      _.get(forwardsAction, 'action') === RestModalAction.cancel_with_link
    ) {
      executeEvents({
        synchronous: true,
        events: [
          {
            ...commonEvent,
            event: 'cancel',
          },
        ],
        syncs: [
          {
            ...syncObj,
            url: forwardsAction.url,
            action: RestModalAction.link,
          },
        ],
      });
    } else if (_.get(forwardsAction, 'action') === 'nevermind') {
      const eventObj = {
        session: syncData.session,
        version: syncData.version,
        app_id: syncData.app_id,
        id: card.id,
        name: card.name,
        event: 'nevermind',
        properties: {
          survey_state: syncData.survey_state,
        },
      };

      const syncObj: EventSyncObject = {
        session: syncData.session,
        version: syncData.version,
        app_id: syncData.app_id,
        id: card.id,
        name: card.name,
        direction: 'backwards',
        type: action.type,
        action: RestModalAction.link,
        url: action.url,
        validation: action.validation,
        form: action.sync,
        survey_state: syncData.survey_state,
      };

      executeEvents({
        synchronous: true,
        events: [eventObj],
        syncs: [syncObj],
      });
      return;
    } else {
      executeEvents({
        synchronous: true,
        events: [laAcceptedEventJSON, commonEvent],
        syncs: [
          {
            ...syncObj,
            action: RestModalAction.accept_offer,
          },
          syncObj,
        ],
      });
      return;
    }
  };

  const refsPrefix = REFS_CONTEXT_FUNNEL_PREFIX;
  const refsCtx = useContext(RefsContext);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isNoneLayout = lossAversionLayout === 'A';
  useEffect(() => {
    if (isNoneLayout) {
      refsCtx?.removeRef(`${refsPrefix}-la`);
    } else {
      refsCtx?.addRef(`${refsPrefix}-la`, sectionRef);
    }
  }, [isNoneLayout, sectionRef]);

  const hasLACards = !!cards.find(isLossAversionCard);
  const fallbackLaLayout = hasLACards
    ? [
        PlacementLossAversionPosition.loss_aversion_left,
        PlacementLossAversionPosition.loss_aversion_right,
      ]
    : [];
  const laCardPositionsLayout =
    EligibleLALayouts.find((l) => l.label === lossAversionLayout)?.layout ??
    fallbackLaLayout;

  const isLongLayout = !!EligibleLALayouts.filter((l) => l.longable)
    .map((l) => l.layout)
    .find((l) => hasSameElements(l, laCardPositionsLayout));

  return (
    <div className="bbk-loss-components" ref={sectionRef}>
      {isLossAversionLoading && !isDesigner && (
        <LoadingContainer>
          <span className="bbk-fde-loader-spinner">
            {icons.loaderSW6} Loss Aversion cards loading...
          </span>
        </LoadingContainer>
      )}
      {(!isLossAversionLoading || isDesigner) && (
        <LossComponentsGrid isMobile={mobileView}>
          {laCardPositionsLayout.map((position, idx) => {
            const card = cards.find((c) => c.position === position);
            const isLongCard =
              isLongLayout &&
              ((position === PlacementLossAversionPosition.loss_aversion_left &&
                !laCardPositionsLayout.includes(
                  PlacementLossAversionPosition.loss_aversion_bottom_left
                )) ||
                (position ===
                  PlacementLossAversionPosition.loss_aversion_right &&
                  !laCardPositionsLayout.includes(
                    PlacementLossAversionPosition.loss_aversion_bottom_right
                  )));
            const isBottomRow =
              position ===
                PlacementLossAversionPosition.loss_aversion_bottom_left ||
              position ===
                PlacementLossAversionPosition.loss_aversion_bottom_right ||
              position ===
                PlacementLossAversionPosition.loss_aversion_bottom_row;
            const laCardDims = getLaCardDims(position, isLongCard, mobileView);
            const hasError = !!lossAversionErrors?.includes(position);
            return (
              <LossAversionCardContainer
                key={position}
                laCardDims={laCardDims}
                needsGridRow={!mobileView && (isLongCard || isBottomRow)}
              >
                <DesignElement
                  name={`Loss card`}
                  noCard={!card}
                  laPosition={position}
                >
                  {card ? (
                    <CardFactory
                      card={card}
                      handleLossCardAction={({ action }) => {
                        handleLossCardAction({ card, action } as {
                          card: RestLossAversionWithOffer;
                          action: RestModalForwards;
                        });
                      }}
                      isHorizontal={
                        !mobileView && HorizontalLaPositions.includes(position)
                      }
                    />
                  ) : isDesigner ? (
                    <EmptyLACard slot={position} hasError={hasError} />
                  ) : (
                    <EmptyLACardLive
                      isHorizontal={HorizontalLaPositions.includes(position)}
                    />
                  )}
                </DesignElement>
              </LossAversionCardContainer>
            );
          })}
        </LossComponentsGrid>
      )}
    </div>
  );
};
